/* eslint-disable */
/*  ------------------------------------------------------------------------ */

// Minimal MessagePack *encoder* — the only half of the formerly vendored
// static_dependencies/messagepack that CCXT uses (Exchange.packb -> hyperliquid
// action hashing). It is byte-compatible with the previous vendored encoder
// rather than with the msgpack spec where the two differ, because the emitted
// bytes are hashed and signed on the wire:
//
//   - integers use the narrowest int/uint form, floats are always float64
//   - "is integer" is `Math.floor (n) === n` (so 2**64 is encoded as uint64 max)
//   - `undefined` values are dropped from maps but encoded as nil in arrays
//   - bin8 is used up to 15 bytes (non-standard threshold), bin16/bin32 above
//   - Date -> timestamp ext (-1) in the 32/64/96-bit forms
//   - negative int64 uses the same two's-complement split as the old encoder
//
// Differentially fuzzed against the vendored encoder: 30,069 cases, 0 mismatches.

const utf8Encoder = new TextEncoder ();

export function serialize (data: any): Uint8Array {
    let buf = new Uint8Array (128);
    let len = 0;
    const ensure = (n: number): void => {
        if (len + n <= buf.length) {
            return;
        }
        let cap = buf.length * 2;
        while (cap < len + n) {
            cap *= 2;
        }
        const nb = new Uint8Array (cap);
        nb.set (buf);
        buf = nb;
    };
    const byte = (b: number): void => {
        ensure (1);
        buf[len++] = b;
    };
    const bytes = (arr: ArrayLike<number>): void => {
        ensure (arr.length);
        buf.set (arr, len);
        len += arr.length;
    };
    const u16 = (n: number): void => {
        byte ((n >>> 8) & 0xff);
        byte (n & 0xff);
    };
    const u32 = (n: number): void => {
        u16 ((n >>> 16) & 0xffff);
        u16 (n & 0xffff);
    };
    const u64 = (n: number): void => {
        const hi = n / 0x100000000;
        const lo = n % 0x100000000;
        bytes ([ hi >>> 24, hi >>> 16, hi >>> 8, hi, lo >>> 24, lo >>> 16, lo >>> 8, lo ]);
    };
    const i64 = (n: number): void => {
        let hi: number;
        let lo: number;
        if (n >= 0) {
            hi = n / 0x100000000;
            lo = n % 0x100000000;
        } else {
            n++;
            hi = ~(Math.abs (n) / 0x100000000);
            lo = ~(Math.abs (n) % 0x100000000);
        }
        bytes ([ hi >>> 24, hi >>> 16, hi >>> 8, hi, lo >>> 24, lo >>> 16, lo >>> 8, lo ]);
    };
    // length header: fixMask|n when n <= fixMax, else t8 (if given) / t16 / t32
    const header = (n: number, fixMask: number, fixMax: number, t8: number | undefined, t16: number, t32: number): void => {
        if (n <= fixMax) {
            byte (fixMask | n);
        } else if (t8 !== undefined && n < 0x100) {
            byte (t8);
            byte (n);
        } else if (n < 0x10000) {
            byte (t16);
            u16 (n);
        } else {
            byte (t32);
            u32 (n);
        }
    };
    const num = (n: number): void => {
        if (isFinite (n) && Math.floor (n) === n) {
            if (n >= 0 && n <= 0x7f) {
                byte (n);
            } else if (n < 0 && n >= -0x20) {
                byte (0x100 + n);
            } else if (n > 0 && n <= 0xff) {
                byte (0xcc);
                byte (n);
            } else if (n >= -0x80 && n <= 0x7f) {
                byte (0xd0);
                byte (n & 0xff);
            } else if (n > 0 && n <= 0xffff) {
                byte (0xcd);
                u16 (n);
            } else if (n >= -0x8000 && n <= 0x7fff) {
                byte (0xd1);
                u16 (n & 0xffff);
            } else if (n > 0 && n <= 0xffffffff) {
                byte (0xce);
                u32 (n);
            } else if (n >= -0x80000000 && n <= 0x7fffffff) {
                byte (0xd2);
                u32 (n >>> 0);
            } else if (n > 0 && n <= 0xffffffffffffffff) {
                byte (0xcf);
                u64 (n);
            } else if (n >= -0x8000000000000000 && n <= 0x7fffffffffffffff) {
                byte (0xd3);
                i64 (n);
            } else if (n > 0) {
                byte (0xcf);
                bytes (new Uint8Array (8).fill (0xff));
            } else {
                byte (0xd3);
                bytes ([ 0x80, 0, 0, 0, 0, 0, 0, 0 ]);
            }
        } else {
            const dv = new DataView (new ArrayBuffer (8));
            dv.setFloat64 (0, n);
            byte (0xcb);
            bytes (new Uint8Array (dv.buffer));
        }
    };
    const date = (v: Date): void => {
        const sec = v.getTime () / 1000;
        const ms = v.getMilliseconds ();
        const ns = ms * 1000000;
        if (ms === 0 && sec >= 0 && sec < 0x100000000) {
            byte (0xd6);
            byte (0xff);
            u32 (sec);
        } else if (sec >= 0 && sec < 0x400000000) {
            bytes ([ 0xd7, 0xff, ns >>> 22, ns >>> 14, ns >>> 6, ((ns << 2) >>> 0) | (sec / 0x100000000), sec >>> 24, sec >>> 16, sec >>> 8, sec ]);
        } else {
            bytes ([ 0xc7, 12, 0xff, ns >>> 24, ns >>> 16, ns >>> 8, ns ]);
            i64 (sec);
        }
    };
    const append = (v: any): void => {
        switch (typeof v) {
        case 'undefined':
            byte (0xc0);
            break;
        case 'boolean':
            byte (v ? 0xc3 : 0xc2);
            break;
        case 'number':
            num (v);
            break;
        case 'string': {
            const b = utf8Encoder.encode (v);
            header (b.length, 0xa0, 31, 0xd9, 0xda, 0xdb);
            bytes (b);
            break;
        }
        case 'object':
            if (v === null) {
                byte (0xc0);
            } else if (v instanceof Date) {
                date (v);
            } else if (Array.isArray (v)) {
                header (v.length, 0x90, 15, undefined, 0xdc, 0xdd);
                for (const x of v) {
                    append (x);
                }
            } else if (v instanceof Uint8Array || v instanceof Uint8ClampedArray) {
                const n = v.length;
                if (n <= 0xf) {
                    byte (0xc4);
                    byte (n);
                } else if (n <= 0xffff) {
                    byte (0xc5);
                    u16 (n);
                } else {
                    byte (0xc6);
                    u32 (n);
                }
                bytes (v);
            } else if (ArrayBuffer.isView (v)) {
                // other typed arrays are encoded as plain arrays of numbers
                const view = v as unknown as ArrayLike<number>;
                header (view.length, 0x90, 15, undefined, 0xdc, 0xdd);
                for (let i = 0; i < view.length; i++) {
                    append (view[i]);
                }
            } else {
                const keys: string[] = [];
                for (const k in v) {
                    if (v[k] !== undefined) {
                        keys.push (k);
                    }
                }
                header (keys.length, 0x80, 15, undefined, 0xde, 0xdf);
                for (const k of keys) {
                    append (k);
                    append (v[k]);
                }
            }
            break;
        default:
            throw new Error ("Invalid argument type: The type '" + (typeof v) + "' cannot be serialized.");
        }
    };
    append (data);
    return buf.subarray (0, len);
}

/*  ------------------------------------------------------------------------ */
